import { useCallback, useEffect, useState } from 'react';
import { onAuthStateChanged } from 'firebase/auth';
import { auth } from '../../firebase';
import { buildApiUrl, buildWorkUrl } from '../../config/env';

// Every endpoint derives identity from the bearer token. Never send a selected UID.
export async function readAccountSettings(url, user, signal) {
  const token = await user.getIdToken();
  if (auth.currentUser?.uid !== user.uid) throw new Error('Your account changed. Please try again.');
  const response = await fetch(url, { headers: { Authorization: `Bearer ${token}` }, signal });
  const data = await response.json();
  if (auth.currentUser?.uid !== user.uid) throw new Error('Your account changed. Please try again.');
  if (!response.ok) throw new Error(data.error?.message || (typeof data.error === 'string' && data.error) || 'Unable to load your account. Please retry.');
  return data;
}

export function useAccountSettings(section) {
  const [user, setUser] = useState(() => auth.currentUser);
  const [resources, setResources] = useState({});
  const [revision, setRevision] = useState(0);
  const refresh = useCallback(() => setRevision(value => value + 1), []);
  useEffect(() => onAuthStateChanged(auth, setUser), []);
  const uid = user?.uid;

  useEffect(() => {
    if (!uid || !['profile', 'usage'].includes(section)) return;
    const controller = new AbortController();
    let active = true;
    const timeout = setTimeout(() => controller.abort(), 15000);
    const paths = { history: buildApiUrl('/profile/history') };
    if (section === 'usage') Object.assign(paths, { credits: buildWorkUrl('/credits'), workUsage: buildWorkUrl('/usage') });
    const save = (key, state) => {
      if (active && auth.currentUser?.uid === uid) {
        setResources(previous => ({ ...previous, [key]: { ...state, uid } }));
      }
    };
    const operations = Object.entries(paths).map(async ([key, url]) => {
      save(key, { loading: true });
      try { save(key, { data: await readAccountSettings(url, user, controller.signal), loading: false }); }
      catch (error) { save(key, { error: controller.signal.aborted ? 'This request timed out. Please retry.' : error.message, loading: false }); }
    });
    Promise.allSettled(operations).then(() => clearTimeout(timeout));
    return () => { active = false; controller.abort(); clearTimeout(timeout); };
  }, [uid, user, section, revision]);

  // Clear the view immediately on account changes, including while an old fetch completes.
  const owned = key => resources[key]?.uid === uid ? resources[key] : uid
    ? { loading: true } : { loading: false, error: 'Sign in to view your account.' };
  return { user, history: owned('history'), credits: owned('credits'), workUsage: owned('workUsage'), refresh };
}
